import React, { useState, useEffect, useCallback } from 'react';
import { housecallProAPI } from '../../lib/housecallproApi';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import {
  AlertCircle, Check, ChevronLeft, Eye, EyeOff, ExternalLink, Loader2,
  RefreshCw, Settings, Unplug, Users, MapPin, Briefcase, Package, BarChart3,
  Clock, Webhook, ShieldAlert, Info,
} from 'lucide-react';
import { toast } from 'sonner';

// ─── Status Badge ───────────────────────────────────────────────────
function StatusBadge({ status }) {
  const cfg = {
    connected: { label: 'Connected', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' },
    invalid_credentials: { label: 'Invalid Credentials', cls: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' },
    disconnected: { label: 'Not Connected', cls: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400' },
    error: { label: 'Error', cls: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' },
  };
  const c = cfg[status] || cfg.disconnected;
  return <Badge className={`text-xs font-medium ${c.cls}`} data-testid="hcp-status-badge">{c.label}</Badge>;
}

// ─── Mapping Summary Card ───────────────────────────────────────────
function DomainSummary({ icon: Icon, label, data }) {
  if (!data) return null;
  const matched = (data.matched || 0) + (data.auto_matched || 0);
  const unmatched = data.pending || 0;
  const total = data.total || 0;
  return (
    <div className="flex items-center justify-between p-3 rounded-lg border bg-background" data-testid={`hcp-summary-${label.toLowerCase()}`}>
      <div className="flex items-center gap-2.5">
        <Icon className="w-4 h-4 text-muted-foreground" />
        <span className="text-sm font-medium">{label}</span>
      </div>
      <div className="flex items-center gap-3 text-xs">
        <span className="tabular-nums text-muted-foreground">{total} total</span>
        <span className="tabular-nums text-emerald-600">{matched} matched</span>
        {unmatched > 0 && <span className="tabular-nums text-amber-600">{unmatched} unmatched</span>}
      </div>
    </div>
  );
}

// ─── Sync Domain Button ─────────────────────────────────────────────
function SyncButton({ domain, label, icon: Icon, disabled }) {
  const [syncing, setSyncing] = useState(false);
  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await housecallProAPI.syncDomain(domain);
      const r = res.data?.result || {};
      if (r.error) {
        toast.error(`Sync ${label} failed: ${r.error}`);
      } else {
        toast.success(`${label}: ${r.created || 0} new, ${r.updated || 0} updated`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || `Sync ${label} failed`);
    } finally { setSyncing(false); }
  };
  return (
    <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" disabled={disabled || syncing} onClick={handleSync} data-testid={`hcp-sync-${domain}`}>
      {syncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
      Sync {label}
    </Button>
  );
}

// ─── Sync Log Row ───────────────────────────────────────────────────
function LogRow({ log }) {
  const levelCfg = {
    info: 'text-emerald-600', warning: 'text-amber-600', error: 'text-red-600',
  };
  const statusCfg = {
    success: { label: 'Success', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
    partial: { label: 'Partial', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
    failed: { label: 'Failed', cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
    running: { label: 'Running', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  };
  const s = statusCfg[log.status] || statusCfg.running;
  const ts = log.started_at ? new Date(log.started_at).toLocaleString() : '';
  return (
    <tr className="text-xs border-b border-border/50 hover:bg-muted/20" data-testid="hcp-log-row">
      <td className="p-2 text-muted-foreground whitespace-nowrap">{ts}</td>
      <td className="p-2 capitalize">{log.event_type}</td>
      <td className="p-2 capitalize">{log.domain || '-'}</td>
      <td className="p-2"><Badge className={`text-[10px] ${s.cls}`}>{s.label}</Badge></td>
      <td className="p-2 max-w-[300px] truncate text-muted-foreground">{log.message || log.error_message || '-'}</td>
      <td className="p-2 text-muted-foreground tabular-nums">{log.records_created || 0}/{log.records_updated || 0}/{log.records_skipped || 0}</td>
    </tr>
  );
}

// ─── Main Component ─────────────────────────────────────────────────
export function HousecallProSettings({ onBack }) {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [summary, setSummary] = useState({});
  const [savingSettings, setSavingSettings] = useState(false);

  const isConnected = config?.connection_status === 'connected';

  const fetchAll = useCallback(async () => {
    try {
      const [statusRes, logsRes, summaryRes] = await Promise.all([
        housecallProAPI.getStatus(),
        housecallProAPI.getLogs({ limit: 20 }),
        housecallProAPI.getMappingSummary(),
      ]);
      setConfig(statusRes.data?.configured ? statusRes.data : null);
      setLogs(logsRes.data?.logs || []);
      setLogsTotal(logsRes.data?.total || 0);
      setSummary(summaryRes.data?.domains || {});
    } catch {
      // Not configured yet
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleConnect = async () => {
    if (!apiKey.trim()) { toast.error('Please enter an API key'); return; }
    setConnecting(true);
    try {
      const res = await housecallProAPI.connect({ api_key: apiKey.trim() });
      toast.success('Connected to Housecall Pro');
      setConfig(res.data);
      setApiKey('');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Connection failed');
    } finally { setConnecting(false); }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      await housecallProAPI.testConnection();
      toast.success('Connection verified');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test failed');
    } finally { setTesting(false); }
  };

  const handleDisconnect = async () => {
    if (!confirm('Disconnect Housecall Pro? Mappings will be preserved but sync will stop.')) return;
    setDisconnecting(true);
    try {
      await housecallProAPI.disconnect();
      toast.success('Disconnected');
      setConfig(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Disconnect failed');
    } finally { setDisconnecting(false); }
  };

  const [importProgress, setImportProgress] = useState(null);

  const handleImport = async () => {
    setImporting(true);
    setImportProgress(null);
    try {
      const res = await housecallProAPI.runImport({ domains: ['customers', 'locations', 'jobs', 'materials'] });
      const taskId = res.data?.task_id;
      if (!taskId) {
        toast.error('Import failed to start');
        setImporting(false);
        return;
      }

      // If already running, use that task
      if (res.data?.status === 'already_running') {
        toast.info('An import is already running. Watching progress...');
      } else {
        toast.info('Import started in background...');
      }

      setImportProgress({ status: 'running', progress: '0/4', current_domain: null });

      // Poll for status
      const poll = async () => {
        try {
          const status = await housecallProAPI.getImportStatus(taskId);
          const d = status.data;
          setImportProgress(d);

          if (d.status === 'completed' || d.status === 'completed_with_errors') {
            const parts = [];
            let hasFailures = false;
            for (const [domain, r] of Object.entries(d.results || {})) {
              if (r.error || r.api_status === 'not_available') {
                parts.push(`${domain}: failed`);
                hasFailures = true;
              }
              else if (r.skipped) parts.push(`${domain}: skipped`);
              else parts.push(`${domain}: ${r.created || 0} new, ${r.updated || 0} updated${r.items_created ? ` (${r.items_created} catalogue items)` : ''}`);
            }
            if (hasFailures) {
              toast.warning(`Import completed with issues: ${parts.join(', ')}`);
            } else {
              toast.success(`Import complete: ${parts.join(', ')}`);
            }
            setImporting(false);
            setImportProgress(null);
            fetchAll();
            return;
          }
          if (d.status === 'failed') {
            toast.error(`Import failed: ${d.error || 'Unknown error'}`);
            setImporting(false);
            setImportProgress(null);
            fetchAll();
            return;
          }
          // Still running — poll again
          setTimeout(poll, 2000);
        } catch {
          toast.error('Lost connection to import task');
          setImporting(false);
          setImportProgress(null);
        }
      };
      setTimeout(poll, 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed to start');
      setImporting(false);
      setImportProgress(null);
    }
  };

  const handleToggle = async (field, value) => {
    setSavingSettings(true);
    try {
      const res = await housecallProAPI.updateSettings({ [field]: value });
      setConfig(prev => ({ ...prev, ...res.data }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update setting');
    } finally { setSavingSettings(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="hcp-settings">
      {/* Header */}
      <div className="flex items-center gap-3">
        {onBack && (
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="hcp-back-btn">
            <ChevronLeft className="w-4 h-4" />
          </Button>
        )}
        <div className="flex-1">
          <h2 className="text-lg font-bold flex items-center gap-2">
            Housecall Pro Integration
            {config && <StatusBadge status={config.connection_status} />}
          </h2>
          <p className="text-sm text-muted-foreground">
            Sync jobs and customers from Housecall Pro. Track materials and inventory in Stock & Load.
          </p>
        </div>
      </div>

      {/* ─── Connection Section ─────────────────────────────────────── */}
      <div className="rounded-lg border p-4 space-y-4" data-testid="hcp-connection-card">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <Settings className="w-4 h-4 text-muted-foreground" /> Connection
          </h3>
          {isConnected && config.api_key_last4 && (
            <span className="text-xs text-muted-foreground font-mono">Key: ****{config.api_key_last4}</span>
          )}
        </div>

        {!isConnected ? (
          <div className="space-y-3">
            <div className="p-3 rounded-md bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900 text-xs text-blue-800 dark:text-blue-300 flex gap-2">
              <Info className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Connect your Housecall Pro account</p>
                <p className="mt-0.5 opacity-80">Enter your HCP API key to sync customers, jobs, and locations. Your inventory stays in Stock & Load.</p>
              </div>
            </div>
            <div>
              <Label className="text-xs">API Key</Label>
              <div className="flex gap-2 mt-1">
                <div className="relative flex-1">
                  <Input type={showKey ? 'text' : 'password'} value={apiKey} onChange={e => setApiKey(e.target.value)}
                    placeholder="Enter Housecall Pro API key" className="pr-10" data-testid="hcp-api-key-input"
                    onKeyDown={e => { if (e.key === 'Enter') handleConnect(); }} />
                  <button onClick={() => setShowKey(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <Button onClick={handleConnect} disabled={connecting || !apiKey.trim()} data-testid="hcp-connect-btn">
                  {connecting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Connect
                </Button>
              </div>
            </div>
            {config?.connection_error && (
              <div className="flex items-start gap-2 p-2.5 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 text-xs text-red-700 dark:text-red-300">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{config.connection_error}</span>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleTest} disabled={testing} data-testid="hcp-test-btn">
              {testing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />} Test Connection
            </Button>
            <Button variant="outline" size="sm" onClick={handleImport} disabled={importing} data-testid="hcp-import-btn">
              {importing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
              {importing && importProgress?.current_domain
                ? `Importing ${importProgress.current_domain}...`
                : importing ? 'Starting import...' : 'Full Import'}
            </Button>
            <div className="flex-1" />
            <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
              onClick={handleDisconnect} disabled={disconnecting} data-testid="hcp-disconnect-btn">
              {disconnecting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Unplug className="w-3 h-3 mr-1" />} Disconnect
            </Button>
          </div>
        )}

        {config?.last_successful_sync_at && (
          <p className="text-[11px] text-muted-foreground flex items-center gap-1">
            <Clock className="w-3 h-3" /> Last sync: {new Date(config.last_successful_sync_at).toLocaleString()}
          </p>
        )}

        {/* Import Progress */}
        {importing && importProgress && (
          <div className="p-3 rounded-md bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900 space-y-2" data-testid="hcp-import-progress">
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium text-blue-800 dark:text-blue-300">
                {importProgress.current_domain
                  ? `Importing ${importProgress.current_domain}...`
                  : importProgress.status === 'queued' ? 'Queued...' : 'Finishing...'}
              </span>
              <span className="text-blue-600 dark:text-blue-400 tabular-nums">{importProgress.progress}</span>
            </div>
            <div className="w-full bg-blue-200/50 dark:bg-blue-800/30 rounded-full h-1.5">
              <div className="bg-blue-500 h-1.5 rounded-full transition-all duration-500"
                style={{ width: `${importProgress.progress ? (parseInt(importProgress.progress.split('/')[0]) / parseInt(importProgress.progress.split('/')[1]) * 100) : 0}%` }} />
            </div>
            {Object.keys(importProgress.results || {}).length > 0 && (
              <div className="text-[10px] text-blue-700 dark:text-blue-400 space-y-0.5">
                {Object.entries(importProgress.results).map(([domain, r]) => (
                  <div key={domain}>
                    {domain}: {(r.error || r.api_status === 'not_available') 
                      ? <span className="text-red-600">{`failed — ${r.error || 'API not available'}`}</span>
                      : `${r.created || 0} new, ${r.updated || 0} updated${r.items_created ? ` (${r.items_created} items added to catalogue)` : ''}`}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Only show remaining sections when connected */}
      {isConnected && (
        <>
          {/* ─── Sync Scope ─────────────────────────────────────────── */}
          <div className="rounded-lg border p-4 space-y-4" data-testid="hcp-sync-scope">
            <h3 className="text-sm font-semibold">Sync Scope</h3>
            <div className="space-y-3">
              {[
                { field: 'sync_customers', label: 'Customers', icon: Users, desc: 'Sync customer records from HCP' },
                { field: 'sync_locations', label: 'Locations / Warehouses', icon: MapPin, desc: 'Map HCP locations to Stock & Load warehouses and van/truck inventory' },
                { field: 'sync_jobs', label: 'Jobs', icon: Briefcase, desc: 'Sync jobs from HCP for material tracking' },
                { field: 'sync_materials', label: 'Materials / Catalog', icon: Package, desc: 'Import catalog items where supported by your HCP plan. Coverage may vary.' },
                { field: 'writeback_material_usage', label: 'Write-back Material Usage', icon: ExternalLink, desc: 'Push material usage summaries back to HCP jobs (optional, never blocks deductions)' },
              ].map(({ field, label, icon: Icon, desc }) => (
                <div key={field} className="flex items-center justify-between gap-4 py-1">
                  <div className="flex items-start gap-2.5 flex-1 min-w-0">
                    <Icon className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{label}</p>
                      <p className="text-[11px] text-muted-foreground">{desc}</p>
                    </div>
                  </div>
                  <Switch checked={config?.[field] ?? false} disabled={savingSettings}
                    onCheckedChange={v => handleToggle(field, v)} data-testid={`hcp-toggle-${field}`} />
                </div>
              ))}
            </div>
          </div>

          {/* ─── Per-Domain Sync Actions ─────────────────────────────── */}
          <div className="rounded-lg border p-4 space-y-3" data-testid="hcp-domain-sync">
            <h3 className="text-sm font-semibold">Manual Sync</h3>
            <p className="text-xs text-muted-foreground">Re-sync individual domains on demand.</p>
            <div className="flex flex-wrap gap-2">
              <SyncButton domain="customers" label="Customers" icon={Users} disabled={!config?.sync_customers} />
              <SyncButton domain="locations" label="Locations" icon={MapPin} disabled={!config?.sync_locations} />
              <SyncButton domain="jobs" label="Jobs" icon={Briefcase} disabled={!config?.sync_jobs} />
              <SyncButton domain="materials" label="Materials" icon={Package} disabled={!config?.sync_materials} />
            </div>
          </div>

          {/* ─── Mapping Summary ─────────────────────────────────────── */}
          <div className="rounded-lg border p-4 space-y-3" data-testid="hcp-mapping-summary">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-muted-foreground" /> Mapping Summary
              </h3>
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={fetchAll}>
                <RefreshCw className="w-3 h-3 mr-1" /> Refresh
              </Button>
            </div>
            <div className="space-y-2">
              <DomainSummary icon={Users} label="Customers" data={summary.customer} />
              <DomainSummary icon={MapPin} label="Locations" data={summary.location} />
              <DomainSummary icon={Briefcase} label="Jobs" data={summary.job} />
              <DomainSummary icon={Package} label="Materials" data={summary.material} />
              {Object.keys(summary).length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-3">No mappings yet. Run an import to get started.</p>
              )}
            </div>
          </div>

          {/* ─── Webhook Instructions ────────────────────────────────── */}
          <div className="rounded-lg border p-4 space-y-3" data-testid="hcp-webhook-card">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Webhook className="w-4 h-4 text-muted-foreground" /> Webhook Setup
            </h3>
            <div className="p-3 rounded-md bg-muted/30 text-xs space-y-2">
              <p>To enable real-time sync, configure a webhook in your Housecall Pro account:</p>
              <div className="bg-background rounded border p-2 font-mono text-[11px] break-all select-all"
                data-testid="hcp-webhook-url">
                {window.location.origin}/api/integrations/housecall-pro/webhook
              </div>
              {config?.webhook_secret && (
                <div>
                  <p className="text-muted-foreground mb-1">Webhook Secret (include as X-Webhook-Secret header):</p>
                  <div className="bg-background rounded border p-2 font-mono text-[11px] break-all select-all"
                    data-testid="hcp-webhook-secret">
                    {config.webhook_secret}
                  </div>
                </div>
              )}
              <p className="text-muted-foreground">
                Subscribe to: customer.created, customer.updated, job.created, job.updated, job.completed
              </p>
            </div>
          </div>

          {/* ─── Sync Logs ───────────────────────────────────────────── */}
          <div className="rounded-lg border p-4 space-y-3" data-testid="hcp-sync-logs">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Clock className="w-4 h-4 text-muted-foreground" /> Sync History
              </h3>
              <span className="text-xs text-muted-foreground">{logsTotal} total events</span>
            </div>
            {logs.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="hcp-logs-table">
                  <thead className="bg-muted/30 text-muted-foreground uppercase tracking-wide text-[10px]">
                    <tr>
                      <th className="text-left p-2">Time</th>
                      <th className="text-left p-2">Type</th>
                      <th className="text-left p-2">Domain</th>
                      <th className="text-left p-2">Status</th>
                      <th className="text-left p-2">Message</th>
                      <th className="text-left p-2">C/U/S</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map(l => <LogRow key={l.id} log={l} />)}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground text-center py-4">No sync events yet</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
